import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam, AnyType } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";

type InternalModelEvaluationListsRow = AnyType;

export interface IGetModelEvaluationListsResponceModel {
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * モデルID
   */
  modelid: string;
  /**
   * モデル名
   */
  name: string;
  /**
   * 整定時間
   */
  settlingtime: number;
  /**
   * 立ち上がり時間
   */
  risetime: number;
  /**
   * オーバーシュート
   */
  overshoot: number;
  /**
   * オフセット
   */
  offset: number;
  /**
   * 安定性
   */
  stability: number;
}

export interface IModelEvaluationListsRow extends IGetModelEvaluationListsResponceModel {
  id: number;
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIからモデル評価結果一覧の取得が成功した場合に呼び出されます
   * @callback success
   * @param {IModelEvaluationListsRow[]} rows - モデル評価結果一覧
   * @param {number} status - httpステータス
   */
  success: (rows: IModelEvaluationListsRow[], status: number) => void;
}

function createRow(index: number, row: InternalModelEvaluationListsRow): IModelEvaluationListsRow {
  row.id = index;
  row.overshoot = Math.round(Number(row.overshoot) * Math.pow(10, 5)) / Math.pow(10, 5);
  row.offset = Math.round(Number(row.offset) * Math.pow(10, 5)) / Math.pow(10, 5);
  row.stability = Math.round(Number(row.stability) * Math.pow(10, 5)) / Math.pow(10, 5);
  return row;
}

function createRows(data: IGetModelEvaluationListsResponceModel[]): IModelEvaluationListsRow[] {
  if (Array.isArray(data)) {
    const rows: InternalModelEvaluationListsRow[] = data.slice();
    rows.forEach((row, i) => {
      row = createRow(i, row);
    });
    return rows;
  } else {
    return [createRow(0, data)];
  }
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: { Authorization: "Bearer " + token },
    });
  } else {
    return axios.get(url);
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
  authService?: AuthService;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => { 
      params.success(createRows(res.data), res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IGetModelEvaluationListsParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
}

/**
 * モデル評価結果一覧を取得します
 * @function getModelEvaluationLists
 * @param {IGetModelEvaluationListsParams} params - モデル評価結果一覧取得パラメータ
 */
export function getModelEvaluationLists(params: IGetModelEvaluationListsParams): void {
  const { authService, projectid } = params;
  const url = WebAPI.EVALUATION_MODEL(projectid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              getModelEvaluationListsRetry401(authService, url, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * モデル評価結果一覧を取得します
 * @function getModelEvaluationListsRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function getModelEvaluationListsRetry401(
  authService: AuthService,
  url: string,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}